import type { GymSubscription } from '@techsofit/api-types';

/** How many days out the app starts nudging the owner about an approaching lapse. */
const WARN_WITHIN_DAYS = 5;

/**
 * A locked subscription puts the gym into read-only mode: existing data stays, but new
 * writes (payments, members, reminders) are blocked until the gym pays. This only happens
 * once a lapse runs past its grace window (or on cancellation) — the backend computes it
 * (grace-aware) and we mirror its `locked` flag rather than re-deriving from status.
 */
export function isSubscriptionLocked(sub: GymSubscription): boolean {
  return sub.locked;
}

/** True while the gym is on the free-trial track (status TRIAL). Paid gyms keep a
 *  historical trialEndsAt but are no longer "on trial". */
export function isFreeTrial(sub: GymSubscription): boolean {
  return sub.status === 'TRIAL';
}

/** Whole days until the trial / paid period ends. Negative once past; null = no end date. */
export function subscriptionDaysLeft(sub: GymSubscription): number | null {
  if (isFreeTrial(sub) && sub.trialDaysLeft != null) return sub.trialDaysLeft;
  if (sub.currentPeriodEnd) {
    const end = new Date(`${sub.currentPeriodEnd}T00:00:00`);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return Math.round((end.getTime() - today.getTime()) / 86_400_000);
  }
  return null;
}

export type SubAlertTone = 'expired' | 'warning';

export interface SubAlert {
  tone: SubAlertTone;
  /** Whole days left (0 on the day it lapses). */
  daysLeft: number;
  freeTrial: boolean;
  title: string;
  body: string;
}

/** What (if anything) to surface about the gym's own subscription right now. */
export function subscriptionAlert(sub: GymSubscription): SubAlert | null {
  const freeTrial = isFreeTrial(sub);
  if (isSubscriptionLocked(sub)) {
    return {
      tone: 'expired',
      daysLeft: 0,
      freeTrial,
      title: freeTrial ? 'Your free trial has ended' : 'Your subscription has ended',
      body: 'Renew to keep recording payments, adding members, and sending reminders.',
    };
  }
  // Lapsed but still inside the grace window — usable for a few more days. Count down hard.
  if (sub.status === 'PAST_DUE' && sub.graceDaysLeft != null) {
    const n = sub.graceDaysLeft;
    const when = n <= 0 ? 'today' : n === 1 ? 'tomorrow' : `in ${n} days`;
    return {
      tone: 'expired',
      daysLeft: n,
      freeTrial,
      title: freeTrial ? 'Your free trial has ended' : 'Your subscription has expired',
      body: `You'll lose access ${when}. Renew now to keep recording payments, adding members, and sending reminders.`,
    };
  }
  const days = subscriptionDaysLeft(sub);
  if (days != null && days >= 0 && days <= WARN_WITHIN_DAYS) {
    const when = days === 0 ? 'today' : days === 1 ? 'tomorrow' : `in ${days} days`;
    return {
      tone: 'warning',
      daysLeft: days,
      freeTrial,
      title: freeTrial ? `Your free trial ends ${when}` : `Your plan renews ${when}`,
      body: freeTrial
        ? 'Choose a plan to keep premium features when it ends.'
        : 'Pay securely to keep things running without interruption.',
    };
  }
  return null;
}
